import { useGetWorkspaceWorkflows } from '@automata/api/apiComponents';
import { Tag, WorkflowType } from '@automata/api/apiSchemas';
import { ListScreensHeader, PageContainer } from '@automata/ui';
import { getErrorMessage, logger } from '@automata/utils';
import { useFlag } from '@automata/utils/hooks';
import { getTimeZoneCode } from '@automata/utils/time';
import Box from '@mui/material/Box';
import { SxProps, Theme } from '@mui/material/styles';
import { GridRowParams } from '@mui/x-data-grid-pro';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';

import ModelList from 'components/ModelList';
import {
  createColumns,
  createRows,
  getDateColumn,
  getMenuColumn,
  getNameColumn,
  getTagsColumn,
} from 'components/ModelList/columns';
import { FilterIds } from 'components/ModelList/components/FilterPanel';
import { ValidColumnIds } from 'components/ModelList/types';
import { Placeholder } from 'components/Placeholder';
import {
  ResourceActions,
  lookupResourceActions,
} from 'components/ResourceActions';
import { CreateResourcePopover } from 'components/ResourceActions/CreateResourcePopover';
import { ResourceActionsModal } from 'components/ResourceActions/ResourceActionsModal';
import { GlobalRoutes } from 'GlobalRoutes';
import { useRootUrl } from 'hooks/useRootUrl';
import { useURLSearchParams } from 'hooks/useURLSearchParams';
import { useWorkspaceID } from 'hooks/useWorkspaceID';
import { ModelNames, UIModelNames, WorkflowTypes } from 'model/types';
import { mutatePathAsUrl } from 'utils/url';

import { getWorkflowBehaviour } from '../../model/WorkflowTypes';
import { isApiWorkflowType } from '../../utils/helpers';

const uniqBy = <T, K extends keyof any>(arr: T[], key: (v: T) => K): T[] => {
  const seen = new Set<K>();
  const res: T[] = [];
  for (const item of arr) {
    const k = key(item);
    if (!seen.has(k)) {
      seen.add(k);
      res.push(item);
    }
  }
  return res;
};

const dataGridContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
};

type RowShape = {
  [ValidColumnIds.ID]: string;
  [ValidColumnIds.NAME]: string;
  [ValidColumnIds.DATE_MODIFIED]: string;
  [ValidColumnIds.WORKFLOWS_TYPE]: WorkflowType;
  [ValidColumnIds.TAGS]: {
    id: string;
    currentTags: Tag[];
    resource: string;
  };
  [ValidColumnIds.WORKFLOWS_MENU]: string;
};

export const WorkflowsList = (): JSX.Element => {
  const router = useRouter();
  const { baseRouteWithOrg } = useRootUrl(GlobalRoutes.workflow);
  const workspaceID = useWorkspaceID();
  const [searchParams] = useURLSearchParams();
  const {
    [FilterIds.NAME]: nameFilter,
    [FilterIds.START_DATE]: dateRangeStartFilter,
    [FilterIds.END_DATE]: dateRangeEndFilter,
    [FilterIds.TAGS]: tagsFilterParam,
  } = searchParams;

  const typesFilterParam = searchParams.types ?? '';

  const typesFilter = useMemo(
    () =>
      typesFilterParam
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    [typesFilterParam]
  );

  const tagsFilter = tagsFilterParam ? tagsFilterParam.split(',') : [];
  const maestroWorkflowEnabled = useFlag('fe.maestro_workflow');
  const tagsEnabled = useFlag('fe.tags');
  const apiBatchingEnabled = useFlag('fe.api_batching');

  const {
    data: currentWorkflows,
    isLoading,
    error,
  } = useGetWorkspaceWorkflows(
    {
      pathParams: { workspaceID: workspaceID || '' },
      queryParams: {
        count: 1000,
        ...(dateRangeStartFilter ? { range_start: dateRangeStartFilter } : {}),
        ...(dateRangeEndFilter ? { range_end: dateRangeEndFilter } : {}),
        ...(nameFilter ? { filter: nameFilter } : {}),
        ...(tagsFilter.length > 0 ? { tags: tagsFilter } : {}),
      },
    },
    { enabled: !!workspaceID }
  );

  const [selectedWorkflowID, setSelectedWorkflowID] = useState<
    string | undefined
  >(undefined);

  const url = new URL(`https://example.com${router.asPath}`);
  const idParam = url.searchParams.get('id') ?? '';
  const tokenParam = url.searchParams.get('token') ?? '';
  const actionParam = url.searchParams.get('action') ?? '';
  const [openModal, setOpenModal] = useState(false);
  const [openPopover, setOpenPopover] = useState(false);
  const [selectedAction, setSelectedAction] = useState<ResourceActions>(
    ResourceActions.CREATE
  );

  const workflowList = useMemo(
    () => currentWorkflows?.workflows ?? [],
    [currentWorkflows?.workflows]
  );

  const typeOptions: string[] = useMemo(() => Object.values(WorkflowTypes), []);

  const tagOptions = useMemo(() => {
    const allTags = (workflowList ?? []).flatMap((w) => w.tags ?? []);
    const unique = uniqBy(allTags, (t) => t.id);
    return unique.map((t) => ({
      id: t.id,
      name: (t as any).name ?? (t as any).label ?? t.id,
    }));
  }, [workflowList]);

  const filteredByType = useMemo(() => {
    if (!typesFilter.length) return workflowList;
    return workflowList.filter((w) => typesFilter.includes(w.type));
  }, [workflowList, typesFilter]);

  useEffect(() => {
    const resourceAction = lookupResourceActions(actionParam);
    const workflow = currentWorkflows?.workflows.find((workflow) => {
      return workflow.id == idParam;
    });

    if (!workflow || !resourceAction || !tokenParam) {
      return;
    }

    setSelectedWorkflowID(workflow.id);
    setSelectedAction(resourceAction);
    setOpenModal(true);
  }, [actionParam, currentWorkflows, idParam, tokenParam]);

  const handleSelectAction = useCallback((action: string) => {
    const resourceAction = lookupResourceActions(action);
    if (!resourceAction) {
      return;
    }
    setSelectedAction(resourceAction);
    setOpenModal(true);
  }, []);

  const columns = useMemo(
    () =>
      createColumns<RowShape>((columnIds) => [
        getNameColumn({
          headerName: 'Workflow name',
          flex: 4,
        }),
        {
          field: columnIds.WORKFLOWS_TYPE,
          headerName: 'Type',
          headerAlign: 'left',
          minWidth: 150,
          flex: 0.5,
          renderCell: ({ row }) =>
            getWorkflowBehaviour(row.workflowsType)?.label,
        },
        getDateColumn({
          field: columnIds.DATE_MODIFIED,
          headerName: `Last modified (${getTimeZoneCode()})`,
          flex: 0.5,
        }),
        ...(tagsEnabled
          ? [
              getTagsColumn({
                field: columnIds.TAGS,
              }),
            ]
          : []),
        getMenuColumn(
          { field: columnIds.WORKFLOWS_MENU },
          {
            resourceType: UIModelNames.WORKFLOW,
            selectResource: setSelectedWorkflowID,
            selectAction: handleSelectAction,
          }
        ),
      ]),
    [handleSelectAction, tagsEnabled]
  );

  const rows = useMemo(
    () =>
      createRows<RowShape>((columnIds) =>
        filteredByType
          .filter((el) => !isApiWorkflowType(el.type) || apiBatchingEnabled)
          .map((el) => ({
            [columnIds.ID]: el.id,
            [columnIds.NAME]: el.name,
            [columnIds.DATE_MODIFIED]: el.last_updated,
            [columnIds.WORKFLOWS_TYPE]: el.type,
            [columnIds.TAGS]: {
              id: el.id,
              currentTags: el.tags,
              resource: 'workflow',
            },
            [columnIds.WORKFLOWS_MENU]: el.id,
          }))
      ),
    [filteredByType, apiBatchingEnabled]
  );

  const onClose = useCallback(() => {
    const path = mutatePathAsUrl(router.asPath, (url) => {
      const qp = url.searchParams;
      qp.delete('id');
      qp.delete('token');
      qp.delete('action');
      return url;
    });

    void router.replace(path, undefined, { shallow: true });
    setOpenModal(false);
  }, [router]);

  const handleRowClick = useCallback(
    ({ row }: GridRowParams<RowShape>) => {
      const workflowType = row[ValidColumnIds.WORKFLOWS_TYPE];
      if (
        workflowType === WorkflowTypes.MAESTRO ||
        isApiWorkflowType(workflowType)
      ) {
        void router.push(`${baseRouteWithOrg}/${row.id}?tab=1`);
      } else {
        void router.push(`${baseRouteWithOrg}/${row.id}`);
      }
    },
    [baseRouteWithOrg, router]
  );

  if (error) {
    logger.error(getErrorMessage(error, 'Error loading workflows'));
    return <Placeholder copy="Error loading workflow" />;
  }

  return (
    <PageContainer>
      <ListScreensHeader
        pageName="Workflows"
        onConfirmAction={() => {
          setOpenPopover(true);
        }}
        popover={(anchorEl) => (
          <CreateResourcePopover
            anochorEl={anchorEl}
            resourceType={ModelNames.WORKFLOW}
            resourceID={selectedWorkflowID}
            open={openPopover}
            onClose={() => {
              setOpenPopover(false);
              onClose();
            }}
          />
        )}
      />
      <Box sx={dataGridContainerSx}>
        <ModelList<RowShape>
          data={{
            columns,
            rows,
            loading: isLoading,
            ...(maestroWorkflowEnabled ? { onRowClick: handleRowClick } : {}),
          }}
          route={baseRouteWithOrg}
          resourceName="workflows"
          filterMode="server"
          enableFilterByTag={tagsEnabled}
          filterPanelExtras={{
            typeOptions,
            tagOptions,
          }}
        />
      </Box>
      <ResourceActionsModal
        resourceAction={selectedAction}
        resourceType={ModelNames.WORKFLOW}
        resourceID={selectedWorkflowID}
        requestAuthorisation={tokenParam}
        open={openModal}
        onClose={onClose}
      />
    </PageContainer>
  );
};

WorkflowsList.displayName = 'WorkflowsList';
